import { c as _c } from "react/compiler-runtime";
import { IcoInfo16 } from '@onefootprint/icons';
import * as ScrollArea from '@radix-ui/react-scroll-area';
import { cx } from 'class-variance-authority';
import type * as CSS from 'csstype';
import type { Property } from 'csstype';
import times from 'lodash/times';
import type React from 'react';
import type { JSX } from 'react';
import type { LabelTooltipProps } from '../label';
import Shimmer from '../shimmer';
import Text from '../text';
import Tooltip from '../tooltip';
import TableFilters from './components/table-filters';
import { tableCellStyles } from './table-styles';
export type TableRow<T> = {
  item: T;
  index: number;
};
export type TableVariant = 'default' | 'compact';
export type TableProps<T> = {
  'aria-label': string;
  columns: {
    id?: string;
    text: string;
    width?: Property.Width;
    tooltip?: LabelTooltipProps;
    justifyContent?: CSS.Property.JustifyContent;
  }[];
  emptyStateText?: string;
  getKeyForRow: (item: T) => string;
  getAriaLabelForRow?: (item: T) => string;
  hideThead?: boolean;
  initialSearch?: string;
  isLoading?: boolean;
  shimmerHeight?: CSS.Property.Height;
  items?: Array<T>;
  onChangeSearchText?: (text: string) => void;
  onRowClick?: (item: T, event: React.MouseEvent<HTMLTableRowElement>) => void;
  renderActions?: () => React.ReactNode;
  renderSubActions?: () => React.ReactNode;
  renderTr: (row: TableRow<T>) => JSX.Element;
  hasRowEmphasis?: (item: T) => boolean;
  searchPlaceholder?: string;
  isSearchPrivate?: boolean;
  variant?: TableVariant;
};
const Table = t0 => {
  const $ = _c(51);
  const {
    "aria-label": ariaLabel,
    columns,
    emptyStateText: t1,
    getKeyForRow,
    getAriaLabelForRow,
    hideThead,
    initialSearch,
    isLoading,
    shimmerHeight: t2,
    items,
    onChangeSearchText,
    onRowClick,
    renderActions,
    renderSubActions,
    renderTr,
    hasRowEmphasis,
    searchPlaceholder: t3,
    isSearchPrivate,
    variant: t4
  } = t0;
  const emptyStateText = t1 === undefined ? "No results" : t1;
  const shimmerHeight = t2 === undefined ? "24px" : t2;
  const searchPlaceholder = t3 === undefined ? "Search..." : t3;
  const variant = t4 === undefined ? "default" : t4;
  const shouldRenderFilters = onChangeSearchText || renderActions || renderSubActions;
  const shouldShowEmptyState = !isLoading && !items?.length;
  const shouldShowData = !isLoading && !!items;
  const columnsCount = columns.length;
  let t5;
  if ($[0] !== initialSearch || $[1] !== isSearchPrivate || $[2] !== onChangeSearchText || $[3] !== renderActions || $[4] !== renderSubActions || $[5] !== searchPlaceholder || $[6] !== shouldRenderFilters) {
    t5 = shouldRenderFilters && <div className="flex mb-3 flex-col gap-4">{(onChangeSearchText || renderActions) && <TableFilters initialValue={initialSearch} onChangeText={onChangeSearchText} placeholder={searchPlaceholder} isPrivate={isSearchPrivate}>{renderActions?.()}</TableFilters>}{renderSubActions?.()}</div>;
    $[0] = initialSearch;
    $[1] = isSearchPrivate;
    $[2] = onChangeSearchText;
    $[3] = renderActions;
    $[4] = renderSubActions;
    $[5] = searchPlaceholder;
    $[6] = shouldRenderFilters;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== columns.length || $[9] !== variant) {
    t6 = cx("fp-table", tableCellStyles(variant), {
      "@max-[48rem]:min-w-[800px]": columns.length > 3,
      "@max-[48rem]:min-w-[480px]": columns.length === 3
    });
    $[8] = columns.length;
    $[9] = variant;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== columns) {
    t7 = columns.map(_temp);
    $[11] = columns;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t7) {
    t8 = <colgroup>{t7}</colgroup>;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== columns || $[16] !== hideThead) {
    t9 = hideThead ? null : <thead><tr className="group">{columns.map(_temp2)}</tr></thead>;
    $[15] = columns;
    $[16] = hideThead;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== columns.length || $[19] !== columnsCount || $[20] !== emptyStateText || $[21] !== getAriaLabelForRow || $[22] !== getKeyForRow || $[23] !== hasRowEmphasis || $[24] !== isLoading || $[25] !== items || $[26] !== onRowClick || $[27] !== renderTr || $[28] !== shimmerHeight || $[29] !== shouldShowData || $[30] !== shouldShowEmptyState) {
    t10 = isLoading ? <tbody>{times(2).map(() => <tr key={Math.random()}>{times(columnsCount).map(() => <td key={Math.random()} aria-label="shimmer"><Shimmer aria-hidden={true} height={shimmerHeight} width="100%" className="my-2" /></td>)}</tr>)}</tbody> : <tbody>{shouldShowEmptyState && <tr className="[&>td]:text-left"><td colSpan={columns.length}><Text tag="div" variant="body-2" className="max-w-[calc(100cqw-2rem)] whitespace-normal">{emptyStateText}</Text></td></tr>}{shouldShowData && items.map((item, index) => <tr className="fp-table-row group" aria-label={getAriaLabelForRow ? getAriaLabelForRow(item) : getKeyForRow(item)} data-clickable={!!onRowClick} key={getKeyForRow(item)} onClick={event => {
        onRowClick?.(item, event);
      }} color={hasRowEmphasis?.(item) ? "warning" : undefined}>{renderTr({
          index,
          item
        })}</tr>)}</tbody>;
    $[18] = columns.length;
    $[19] = columnsCount;
    $[20] = emptyStateText;
    $[21] = getAriaLabelForRow;
    $[22] = getKeyForRow;
    $[23] = hasRowEmphasis;
    $[24] = isLoading;
    $[25] = items;
    $[26] = onRowClick;
    $[27] = renderTr;
    $[28] = shimmerHeight;
    $[29] = shouldShowData;
    $[30] = shouldShowEmptyState;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  let t11;
  if ($[32] !== ariaLabel || $[33] !== isLoading || $[34] !== t10 || $[35] !== t6 || $[36] !== t8 || $[37] !== t9 || $[38] !== variant) {
    t11 = <table aria-live="polite" aria-busy={isLoading} aria-label={ariaLabel} data-variant={variant} className={t6}>{t8}{t9}{t10}</table>;
    $[32] = ariaLabel;
    $[33] = isLoading;
    $[34] = t10;
    $[35] = t6;
    $[36] = t8;
    $[37] = t9;
    $[38] = variant;
    $[39] = t11;
  } else {
    t11 = $[39];
  }
  let t12;
  if ($[40] !== ariaLabel || $[41] !== t11) {
    t12 = <ScrollArea.Viewport tabIndex={0} role="region" aria-label={ariaLabel} className="w-full max-w-full focus-visible:outline focus-visible:outline-offset-[-2px] focus-visible:outline-primary">{t11}</ScrollArea.Viewport>;
    $[40] = ariaLabel;
    $[41] = t11;
    $[42] = t12;
  } else {
    t12 = $[42];
  }
  let t13;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      position: "relative"
    };
    $[43] = t13;
  } else {
    t13 = $[43];
  }
  let t14;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <ScrollArea.Scrollbar orientation="horizontal" style={t13} className="flex h-3 touch-none select-none bg-secondary p-0.5"><ScrollArea.Thumb className="relative rounded-full bg-(--border-color-primary)" /></ScrollArea.Scrollbar>;
    $[44] = t14;
  } else {
    t14 = $[44];
  }
  let t15;
  if ($[45] !== t12 || $[46] !== variant) {
    t15 = <ScrollArea.Root type="auto" data-variant={variant} className="fp-table-scroll @container relative w-full min-w-0 max-w-full overflow-hidden">{t12}{t14}</ScrollArea.Root>;
    $[45] = t12;
    $[46] = variant;
    $[47] = t15;
  } else {
    t15 = $[47];
  }
  let t16;
  if ($[48] !== t15 || $[49] !== t5) {
    t16 = <>{t5}{t15}</>;
    $[48] = t15;
    $[49] = t5;
    $[50] = t16;
  } else {
    t16 = $[50];
  }
  return t16;
};
export default Table;
function _temp(column) {
  return <col key={column.id || column.text} style={{
    width: column.width
  }} />;
}
function _temp2(column_0) {
  return <th key={column_0.id || column_0.text}><div className={cx("flex items-center whitespace-nowrap @max-[48rem]:whitespace-normal gap-2", {
      "justify-start": column_0.justifyContent === "flex-start",
      "justify-center": column_0.justifyContent === "center",
      "justify-end": column_0.justifyContent === "flex-end"
    })}><span className="uppercase">{column_0.text}</span>{column_0?.tooltip && <Tooltip disabled={!column_0.tooltip.text} content={column_0.tooltip.text}><IcoInfo16 /></Tooltip>}</div></th>;
}